import dayjs, { Dayjs } from 'dayjs';

type BooleanRecord<T> = { [K in keyof T]: boolean };

const isOneOf = <T extends string>(value: string, allowedValues: readonly T[]): value is T =>
  allowedValues.some((allowedValue) => allowedValue === value);

export const parseEnumParam = <T extends string>(value: string | null, allowedValues: readonly T[]): T | null =>
  value !== null && isOneOf(value, allowedValues) ? value : null;

export const serializeEnum = (value: string | null): string | undefined => value ?? undefined;

// Accept only UTC timestamps in the format produced by toISOString().
// Reject invalid dates, including dates JavaScript would automatically correct (e.g. February 31).
export const parseDateParam = (value: string | null): Dayjs | null => {
  if (value === null) return null;

  const date = dayjs(value);
  return date.isValid() && date.toISOString() === value ? date : null;
};

export const serializeDate = (value: Dayjs | null): string | undefined => value?.toISOString();

// Parses 'admin,user' into { admin: true, user: true }.
// The generic overload preserves the exact return type, e.g. Record<'admin' | 'user', boolean>,
// instead of the broader Record<string, boolean>, while keeping the implementation cast-free.
export function parseParamToBooleanRecord<K extends string>(
  value: string | null,
  defaults: Record<K, boolean>
): Record<K, boolean>;

export function parseParamToBooleanRecord(
  value: string | null,
  defaults: Record<string, boolean>
): Record<string, boolean> {
  if (!value) return defaults;

  const selectedKeys = new Set(value.split(','));
  const result = { ...defaults };

  for (const key in result) {
    result[key] = selectedKeys.has(key);
  }

  return result;
}

// Serializes the boolean filter state into a URL param, e.g.
// { admin: true, user: true, installer: false } → 'admin,user'.
// Returns undefined when nothing is selected, so the param can be removed from the URL.
export const serializeBooleanRecordToParam = <T extends BooleanRecord<T>>(record: T): string | undefined => {
  const selectedKeys = Object.entries(record)
    .filter(([, isEnabled]) => isEnabled)
    .map(([key]) => key);

  return selectedKeys.length > 0 ? selectedKeys.join(',') : undefined;
};
